import { BankPaymentMethod, Card, CardReader } from "gen/payments/models_pb"
import { Money } from "shared-ts/classes/money"
import { UUID } from "shared-ts/classes/uuid"
import { OptionTypes } from "shared-ts/types/tender/tender.types"

import { ManuallyEnteredCard } from "lib/manuallyEnteredCard"

import { Option } from "./option.types"
import { ValidationError } from "./validators"

export type StrictOmit<T, K extends keyof T> = Omit<T, K>

export const StateTypes = {
  SINGLE: "single",
  SPLIT: "split",
} as const

export type StateType = (typeof StateTypes)[keyof typeof StateTypes]

export interface State {
  isSplit: boolean
  customAmount: Money | undefined
  isOptionExplicitlySelected: boolean
  optionState?: OptionState
  inputValidationError: ValidationError
  optionValidationError: ValidationError
  customAmountValidationError: ValidationError
}

export interface SubmissionData {
  submissionOption: SubmissionOption
  amount: Money
}

export type OptionState =
  | ChargeAccountOptionState
  | CheckOptionState
  | CardOptionState
  | WriteOffOptionState
  | CashOptionState
  | CustomOptionState
  | ACHOptionState
  | GiftCardOptionState

export interface BaseOptionState {
  readonly optionID: UUID
}

// convenience wrapper types for backward compatibility where no option id is present.
export type BaseSubmissionOption<T extends OptionState> = StrictOmit<
  T,
  "optionID"
> & {
  optionID?: UUID
}

export type CardSubmissionOption = {
  cardSurchargeAmount: Money
  cardSurchargeTax: Money // being deprecated
  cardSurchargeTaxByTaxRateId: Record<string, Money> // only used if flag is on
} & (
  | BaseSubmissionOption<CardPresentOptionState>
  | BaseSubmissionOption<ManuallyEnteredCardOptionState>
  | BaseSubmissionOption<PreviousCardOptionState>
  | BaseSubmissionOption<PreviousInteracCardOptionState>
  | BaseSubmissionOption<SavedCardOptionState>
)

export interface CustomSubmissionOption
  extends BaseSubmissionOption<CustomOptionState> {
  note: string
  name: string
  isNoteRequired: boolean
}
export type WriteOffSubmissionOption = BaseSubmissionOption<WriteOffOptionState>
export type CashSubmissionOption = BaseSubmissionOption<CashOptionState>
export type CheckSubmissionOption = BaseSubmissionOption<CheckOptionState>
export type ChargeAccountSubmissionOption =
  BaseSubmissionOption<ChargeAccountOptionState>
export type ACHSubmissionOption =
  | BaseSubmissionOption<SavedACHOptionState>
  | BaseSubmissionOption<PreviousACHOptionState>

export type GiftCardSubmissionOption = BaseSubmissionOption<GiftCardOptionState>

export type SubmissionOption =
  | CardSubmissionOption
  | CustomSubmissionOption
  | ChargeAccountSubmissionOption
  | CheckSubmissionOption
  | WriteOffSubmissionOption
  | CashSubmissionOption
  | ACHSubmissionOption
  | GiftCardSubmissionOption

export type AdditionalOptionState<T extends OptionState> = StrictOmit<
  T,
  "optionID"
>

export interface ChargeAccountOptionState extends BaseOptionState {
  type: typeof OptionTypes.CHARGE_ACCOUNT
}

export interface CheckOptionState extends BaseOptionState {
  type: typeof OptionTypes.CHECK
  checkNumber: string
}

export type CardOptionState =
  | SavedCardOptionState
  | CardPresentOptionState
  | ManuallyEnteredCardOptionState
  | PreviousCardOptionState
  | PreviousInteracCardOptionState

export interface BaseCardOptionState extends BaseOptionState {
  type: typeof OptionTypes.CARD
}

export const CardSubOptionTypes = {
  CARD_PRESENT: "card_present",
  MANUALLY_ENTERED_CARD: "manually_entered_card",
  SAVED_CARD: "saved_card",
  PREVIOUS_CARD: "previous_card",
  PREVIOUS_INTERAC_CARD: "previous_interac_card",
} as const

export type CardSubOptionType =
  (typeof CardSubOptionTypes)[keyof typeof CardSubOptionTypes]

export interface NewSavedCardState extends BaseCardOptionState {
  cardNickName: string
  shouldSaveCard: boolean
}

export interface SavedCardOptionState extends BaseCardOptionState {
  subtype: typeof CardSubOptionTypes.SAVED_CARD
  card?: Card
}

export interface CardPresentOptionState extends NewSavedCardState {
  type: typeof OptionTypes.CARD
  subtype: typeof CardSubOptionTypes.CARD_PRESENT
  isInterac: boolean
  postalCode: string
}

export interface ManuallyEnteredCardOptionState extends NewSavedCardState {
  type: typeof OptionTypes.CARD
  subtype: typeof CardSubOptionTypes.MANUALLY_ENTERED_CARD
  manuallyEnteredCard: ManuallyEnteredCard
}

export interface PreviousCardOptionState extends BaseCardOptionState {
  type: typeof OptionTypes.CARD
  subtype: typeof CardSubOptionTypes.PREVIOUS_CARD
}

export interface PreviousInteracCardOptionState extends BaseCardOptionState {
  type: typeof OptionTypes.CARD
  subtype: typeof CardSubOptionTypes.PREVIOUS_INTERAC_CARD
}

export interface WriteOffOptionState extends BaseOptionState {
  type: typeof OptionTypes.WRITE_OFF
}

export interface GiftCardOptionState extends BaseOptionState {
  type: typeof OptionTypes.GIFT_CARD
  giftCardId?: UUID
}

export interface CashOptionState extends BaseOptionState {
  type: typeof OptionTypes.CASH
  cashReceived: Money
  cashRoundingDelta?: Money
}

export interface CustomOptionState extends BaseOptionState {
  type: typeof OptionTypes.CUSTOM
  note: string
}

export const ACHSubOptionTypes = {
  SAVED_ACH: "saved_ach",
  PREVIOUS_ACH: "previous_ach",
} as const

export type ACHSubOptionType =
  (typeof ACHSubOptionTypes)[keyof typeof ACHSubOptionTypes]

export interface BaseACHOptionState extends BaseOptionState {
  type: typeof OptionTypes.ACH
}

export interface SavedACHOptionState extends BaseACHOptionState {
  subtype: typeof ACHSubOptionTypes.SAVED_ACH
  bankAccount?: BankPaymentMethod
}

export interface PreviousACHOptionState extends BaseACHOptionState {
  subtype: typeof ACHSubOptionTypes.PREVIOUS_ACH
}

export type ACHOptionState = SavedACHOptionState | PreviousACHOptionState

export const ActionTypes = {
  SELECT_OPTION: "SET_INITIAL_STATE",
  SET_CUSTOM_AMOUNT: "SET_CUSTOM_AMOUNT",
  TOGGLE_SPLIT: "TOGGLE_SPLIT",
  RESET_FOR_NEXT_SPLIT: "RESET_FOR_NEXT_SPLIT",
  RESET_FOR_CUSTOMER_CHANGE: "RESET_FOR_CUSTOMER_CHANGE",

  SET_SIGNATURE_READER: "SET_SIGNATURE_READER",

  SET_CHECK_NUMBER: "SET_CHECK_NUMBER",

  SET_CARD_PREVIOUS_OPTION: "SET_CARD_PREVIOUS",
  SET_PREVIOUS_INTERAC_OPTION: "SET_PREVIOUS_INTERAC",

  SET_CARD_SAVED_OPTION: "SELECT_CAVED_CARD_OPTION",
  SET_CARD_SAVED: "SET_SAVED_CARD",

  SET_CARD_PRESENT_OPTION: "SET_CARD_PRESENT",
  SET_CARD_PRESENT_READER: "SET_CARD_PRESENT_READER",
  SET_CARD_PRESENT_NAME: "SET_CARD_NICKNAME",
  SET_CARD_PRESENT_POSTAL_CODE: "SET_CARD_PRESENT_POSTAL_CODE",
  SET_CARD_PRESENT_INTERAC: "SET_CARD_PRESENT_INTERAC",
  TOGGLE_CARD_PRESENT_SAVE: "SET_SHOULD_SAVE_CARD",

  SET_CARD_MANUAL_OPTION: "SELECT_MANUALLY_ENTERED_CARD_OPTION",
  SET_CARD_MANUAL: "SET_MANUALLY_ENTERED_CARD",
  SET_CARD_MANUAL_NAME: "SET_MANUALLY_ENTERED_CARD_NICKNAME",
  TOGGLE_CARD_MANUAL_SAVE: "SET_MANUALLY_ENTERED_CARD_SHOULD_SAVE_CARD",

  SET_CASH_RECEIVED: "SET_CASH_RECEIVED",

  SET_CUSTOM_NOTE: "SET_CUSTOM_NOTE",

  SET_INPUT_VALIDATION_ERROR: "SET_INPUT_VALIDATION_ERROR",
  SET_OPTION_BASE_VALIDATION_ERROR: "SET_OPTION_BASE_VALIDATION_ERROR",
  CLEAR_VALIDATION_ERRORS: "CLEAR_VALIDATION_ERRORS",

  SET_ACH_SAVED_OPTION: "SET_ACH_SAVED_OPTION",
  SET_ACH_PREVIOUS_OPTION: "SET_ACH_PREVIOUS_OPTION",
  SET_ACH_SAVED: "SET_ACH_SAVED",

  SET_GIFT_CARD: "SET_GIFT_CARD",
  CLEAR_GIFT_CARD: "CLEAR_GIFT_CARD",
} as const

export type ActionType = (typeof ActionTypes)[keyof typeof ActionTypes]

export type Action =
  | AllAction
  | ChargeAction
  | CheckAction
  | CashAction
  | CustomAction
  | PreviousCardAction
  | SavedCardAction
  | PresentCardAction
  | ManualCardAction
  | ACHAction
  | GiftCardAction
  | ValidationAction

type AllAction =
  | SetInitalOptionAction
  | SetCustomAmountAction
  | ToggleSplitAction
  | ResetForNextSplitAction
  | ResetForCustomerChangeAction

export interface SetInitalOptionAction {
  type: typeof ActionTypes.SELECT_OPTION
  option: Option
  isExplicitlySelected: boolean
  previousCardSubtype?:
    | typeof CardSubOptionTypes.PREVIOUS_CARD
    | typeof CardSubOptionTypes.PREVIOUS_INTERAC_CARD
}

export interface SetCustomAmountAction {
  type: typeof ActionTypes.SET_CUSTOM_AMOUNT
  amount: Money
  allowZero: boolean
  remaining: Money
  requireNickelIfPartial: boolean
}

export interface ToggleSplitAction {
  type: typeof ActionTypes.TOGGLE_SPLIT
}

export interface ResetForNextSplitAction {
  type: typeof ActionTypes.RESET_FOR_NEXT_SPLIT
}

export interface ResetForCustomerChangeAction {
  type: typeof ActionTypes.RESET_FOR_CUSTOMER_CHANGE
}

type ChargeAction = SetSignatureReaderAction

export interface SetSignatureReaderAction {
  type: typeof ActionTypes.SET_SIGNATURE_READER
  reader: CardReader
}

type CheckAction = SetCheckNumberAction

export interface SetCheckNumberAction {
  type: typeof ActionTypes.SET_CHECK_NUMBER
  checkNumber: string
}

type CashAction = SetCashReceivedAction

export interface SetCashReceivedAction {
  type: typeof ActionTypes.SET_CASH_RECEIVED
  cashReceived: Money
}

type CustomAction = SetCustomNoteAction
export interface SetCustomNoteAction {
  type: typeof ActionTypes.SET_CUSTOM_NOTE
  note: string
}

type PreviousCardAction = SetPreviousCardAction | SetInteracPreviousCardAction

export interface SetPreviousCardAction {
  type: typeof ActionTypes.SET_CARD_PREVIOUS_OPTION
}

export interface SetInteracPreviousCardAction {
  type: typeof ActionTypes.SET_PREVIOUS_INTERAC_OPTION
}

type SavedCardAction = SelectCavedCardOptionAction | SetSavedCardAction

// Saved card
export interface SelectCavedCardOptionAction {
  type: typeof ActionTypes.SET_CARD_SAVED_OPTION
}

export interface SetSavedCardAction {
  type: typeof ActionTypes.SET_CARD_SAVED
  card: Card
}

type PresentCardAction =
  | SetCardPresentAction
  | SetCardPresentReaderAction
  | SetPresentCardNicknameAction
  | SetPresentCardPostalCodeAction
  | SetCardPresentInteracAction
  | TogglePresentShouldSaveCardAction
export interface SetCardPresentAction {
  type: typeof ActionTypes.SET_CARD_PRESENT_OPTION
}

export interface SetCardPresentReaderAction {
  type: typeof ActionTypes.SET_CARD_PRESENT_READER
  cardReader: CardReader
}

export interface SetPresentCardNicknameAction {
  type: typeof ActionTypes.SET_CARD_PRESENT_NAME
  nickname: string
}

export interface SetPresentCardPostalCodeAction {
  type: typeof ActionTypes.SET_CARD_PRESENT_POSTAL_CODE
  postalCode: string
}

export interface SetCardPresentInteracAction {
  type: typeof ActionTypes.SET_CARD_PRESENT_INTERAC
  isInterac: boolean
}

export interface TogglePresentShouldSaveCardAction {
  type: typeof ActionTypes.TOGGLE_CARD_PRESENT_SAVE
}

type ManualCardAction =
  | SetManualCardOptionAction
  | SetManuallyEnteredCardAction
  | SetManualCardNicknameAction
  | ToggleManualShouldSaveCardAction

export interface SetManualCardOptionAction {
  type: typeof ActionTypes.SET_CARD_MANUAL_OPTION
}

export interface SetManuallyEnteredCardAction {
  type: typeof ActionTypes.SET_CARD_MANUAL
  manuallyEnteredCard: ManuallyEnteredCard
}

export interface SetManualCardNicknameAction {
  type: typeof ActionTypes.SET_CARD_MANUAL_NAME
  nickname: string
}

export interface ToggleManualShouldSaveCardAction {
  type: typeof ActionTypes.TOGGLE_CARD_MANUAL_SAVE
}

type ACHAction =
  | SetACHSavedOptionAction
  | SetACHSavedAction
  | SetACHPreviousOptionAction

export interface SetACHSavedOptionAction {
  type: typeof ActionTypes.SET_ACH_SAVED_OPTION
}

export interface SetACHSavedAction {
  type: typeof ActionTypes.SET_ACH_SAVED
  bankAccount: BankPaymentMethod
}

export interface SetACHPreviousOptionAction {
  type: typeof ActionTypes.SET_ACH_PREVIOUS_OPTION
}

type GiftCardAction = SetGiftCardAction | ClearGiftCardAction

export interface SetGiftCardAction {
  type: typeof ActionTypes.SET_GIFT_CARD
  giftCardId: UUID
}

export interface ClearGiftCardAction {
  type: typeof ActionTypes.CLEAR_GIFT_CARD
}

type ValidationAction =
  | SetInputValidationErrorAction
  | SetOptionValidationErrorAction
  | ClearValidationErrorsAction

export interface SetInputValidationErrorAction {
  type: typeof ActionTypes.SET_INPUT_VALIDATION_ERROR
  error: string
}

export interface SetOptionValidationErrorAction {
  type: typeof ActionTypes.SET_OPTION_BASE_VALIDATION_ERROR
  error: string
}

export interface ClearValidationErrorsAction {
  type: typeof ActionTypes.CLEAR_VALIDATION_ERRORS
}
